import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { SalarySliceState } from "../../utils/salary.types";
import { ApiStatus } from "../../utils/api.types";
import {
    calculateSalary,
    calculateBulkSalary,
    processSalary,
    fetchSalaries,
    fetchSalaryById,
    updateSalary,
    paySalary,
    deleteSalary,
    calculateDailyWage,
    processDailyWage,
    fetchDailyWages,
    fetchDailyWageById,
    payDailyWage,
    deleteDailyWage
} from "./salaryThunk";

/**
 * Salary slice initial state.
 */
const initialState: SalarySliceState = {
    salaries: [],
    salaryDetails: null,
    salaryCalculation: null,
    errorMessage: '',
    isSalaryProcessed: false,
    selectedSalaryId: '',
    status: ApiStatus.IDLE
}

/**
 * Salary Slice.
 */
const salarySlice = createSlice({
    name: 'salary',
    initialState: initialState,
    reducers: {
        clearSalaryCalculation: (state) => {
            state.salaryCalculation = null;
        },
        clearSalaryDetails: (state) => {
            state.salaryDetails = null;
        },
        setSelectedSalaryId: (state, action) => {
            state.selectedSalaryId = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Calculate Salary (Preview)
            .addCase(calculateSalary.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(calculateSalary.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaryCalculation = action.payload.data;
                state.errorMessage = '';
            })
            .addCase(calculateSalary.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to calculate salary');
            })

            // Calculate Bulk Salary
            .addCase(calculateBulkSalary.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(calculateBulkSalary.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                toast.success('Bulk salary calculated successfully');
            })
            .addCase(calculateBulkSalary.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to calculate bulk salary');
            })

            // Process Salary
            .addCase(processSalary.pending, (state) => {
                state.status = ApiStatus.LOADING;
                state.isSalaryProcessed = false;
            })
            .addCase(processSalary.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.isSalaryProcessed = true;
                state.salaryDetails = action.payload.data;
                state.errorMessage = '';
                toast.success(action.payload.message || 'Salary processed successfully');
            })
            .addCase(processSalary.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.isSalaryProcessed = false;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to process salary');
            })

            // Fetch Salaries
            .addCase(fetchSalaries.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchSalaries.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaries = action.payload.data || [];
                state.errorMessage = '';
            })
            .addCase(fetchSalaries.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })

            // Fetch Salary by ID
            .addCase(fetchSalaryById.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchSalaryById.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaryDetails = action.payload.data;
                state.errorMessage = '';
            })
            .addCase(fetchSalaryById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })

            // Update Salary
            .addCase(updateSalary.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateSalary.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaryDetails = action.payload.data;
                state.errorMessage = '';
                toast.success(action.payload.message || 'Salary updated successfully');
            })
            .addCase(updateSalary.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to update salary');
            })

            // Pay Salary
            .addCase(paySalary.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(paySalary.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaryDetails = action.payload.data;
                state.errorMessage = '';
                toast.success(action.payload.message || 'Salary paid successfully');
            })
            .addCase(paySalary.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to pay salary');
            })

            // Delete Salary
            .addCase(deleteSalary.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteSalary.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                const { id } = action.payload;
                state.salaries = state.salaries.filter(item => item._id !== id);
                toast.success(action.payload.message || 'Salary deleted successfully');
            })
            .addCase(deleteSalary.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to delete salary');
            })

            // ===========================
            // DAILY WAGE CASES
            // ===========================

            // Calculate Daily Wage
            .addCase(calculateDailyWage.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(calculateDailyWage.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaryCalculation = action.payload.data as any;
                state.errorMessage = '';
            })
            .addCase(calculateDailyWage.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to calculate daily wage');
            })

            // Process Daily Wage
            .addCase(processDailyWage.pending, (state) => {
                state.status = ApiStatus.LOADING;
                state.isSalaryProcessed = false;
            })
            .addCase(processDailyWage.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.isSalaryProcessed = true;
                state.salaryDetails = action.payload.data;
                state.errorMessage = '';
                toast.success(action.payload.message || 'Daily wage processed successfully');
            })
            .addCase(processDailyWage.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.isSalaryProcessed = false;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to process daily wage');
            })

            // Fetch Daily Wages
            .addCase(fetchDailyWages.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchDailyWages.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaries = action.payload.data || [];
                state.errorMessage = '';
            })
            .addCase(fetchDailyWages.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })

            // Fetch Daily Wage by ID
            .addCase(fetchDailyWageById.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchDailyWageById.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaryDetails = action.payload.data;
                state.errorMessage = '';
            })
            .addCase(fetchDailyWageById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
            })

            // Pay Daily Wage
            .addCase(payDailyWage.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(payDailyWage.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.salaryDetails = action.payload.data;
                state.errorMessage = '';
                toast.success(action.payload.message || 'Daily wage paid successfully');
            })
            .addCase(payDailyWage.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to pay daily wage');
            })

            // Delete Daily Wage
            .addCase(deleteDailyWage.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteDailyWage.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                const { id } = action.payload;
                state.salaries = state.salaries.filter(item => item._id !== id);
                toast.success(action.payload.message || 'Daily wage deleted successfully');
            })
            .addCase(deleteDailyWage.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message;
                toast.error(action.error.message || 'Failed to delete daily wage');
            });
    }
});

export const { clearSalaryCalculation, clearSalaryDetails, setSelectedSalaryId } = salarySlice.actions;
export default salarySlice.reducer;
